import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, X, Shield, Lock, Eye, Euro, Users, Inbox, FileText, FolderOpen, BarChart3, Sparkles, Zap, Globe, KeyRound, ChevronDown, MessageSquare, Crown, Flame, Send } from 'lucide-react';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import CookieBanner from '../components/CookieBanner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { STATS, PLANS, PERMISSIONS_MATRIX, FEATURE_MATRIX, RYTHMES, prixPeriode } from '../constants/catalog';

function Landing() {
  // Drive the .reveal scroll animations: add .visible when a section enters
  // the viewport (once — sections stay visible after).
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) { els.forEach(el => el.classList.add('visible')); return undefined; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#08080A] text-slate-200 overflow-x-hidden">
      <LandingAnims />
      <Header />
      <Hero />
      <Problem />
      <Spotlights />
      <Everything />
      <Security />
      <Comparison />
      <Pricing />
      <Faq />
      <Cta />
      <Footer />
      <CookieBanner />
    </div>
  );
}

export default Landing;

function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      setHidden(y > 120 && y > lastY);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#features', label: t('nav.features') },
    { href: '#integrations', label: t('nav.why') },
    { href: '#security', label: t('nav.security') },
    { href: '#pricing', label: t('nav.pricing') },
    { href: '#faq', label: t('nav.faq') },
  ];

  return (
    <header
      data-testid="landing-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? 'bg-[#08080A]/90 border-b border-white/10 shadow-xl shadow-black/30' : 'bg-[#08080A]/40 border-b border-transparent'
      } ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
        <Link to="/"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map(l => (
            <a key={l.href} href={l.href} data-testid={`nav-${l.href.slice(1)}`}
               className="relative px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors group">
              {l.label}
              <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-gradient-to-r from-indigo-400 to-violet-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <Link to="/auth/login">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5">{t('nav.login')}</Button>
          </Link>
          <Link to="/subscribe?plan=solo">
            <Button className="bg-white text-[#08080A] hover:bg-slate-200 font-medium shadow-lg shadow-white/5 hover:scale-[1.03] transition-transform">
              {t('nav.getStarted')} <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative grid-bg pt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#08080A] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 fade-in-up">
        <div className="max-w-3xl">
          <Badge variant="outline" className="border-indigo-500/30 bg-gradient-to-r from-indigo-500/15 via-indigo-500/10 to-indigo-500/15 text-indigo-300 mb-6 px-3 py-1 group" data-testid="hero-tagline">
            <span className="relative flex h-2 w-2 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-400" />
            </span>
            {t('brand.tagline')}
            {' '}
            <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] bg-indigo-400/20 text-indigo-200 font-bold uppercase tracking-wider">{t('brand.taglineBadge')}</span>
          </Badge>
          <h1 className="font-display text-5xl md:text-7xl font-bold text-white leading-[1.05] mb-6" data-testid="hero-title">
            {t('hero.line1')}<br />
            {t('hero.line2')}<br />
            <span className="bg-gradient-to-r from-[#9cc0ff] via-[#3f7dff] to-[#73a6ff] bg-clip-text text-transparent">{t('hero.line3')}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-400 mb-8 max-w-2xl leading-relaxed">
            {t('hero.subtitle', { brand: 'DropFans' }).split('DropFans').map((part, i, arr) => (
              <React.Fragment key={i}>
                {part}
                {i < arr.length - 1 && <span className="text-indigo-300 font-semibold">DropFans</span>}
              </React.Fragment>
            ))}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/subscribe?plan=solo">
              <Button data-testid="hero-get-started" size="lg" className="bg-indigo-500 hover:bg-indigo-600 text-white h-12 px-6 font-medium shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-[1.03] transition-all">
                {t('hero.ctaTrial')} <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </Link>
            <a href="#workflow">
              <Button size="lg" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-12 px-6 hover:scale-[1.03] transition-all">
                {t('hero.ctaDemo')}
              </Button>
            </a>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-8 max-w-xl">
            <Stat value="0 %" label="de commission côté agence" />
            <Stat value={STATS.uptime} label={t('hero.stats.uptime')} />
            <Stat value={STATS.syncSpeed} label={t('hero.stats.sync')} />
          </div>
        </div>

        <DemoCard />
      </div>
    </section>
  );
}

// Count-up animation for hero stats: parses the numeric part of the value
// ("3 200+", "99.9%", "<200ms") and animates it from 0 on first view.
function Stat({ value, label }) {
  const [display, setDisplay] = useState(value);
  const ref = React.useRef(null);
  const ranRef = React.useRef(false);   // count up ONCE, then stay — never re-animate

  useEffect(() => {
    // Already animated once? Just show the true value and never restart (a parent
    // re-render — e.g. the auto-rotating demo — must not relaunch the count-up).
    if (ranRef.current) { setDisplay(value); return undefined; }
    const match = String(value).match(/([\d\s,.]*\d)/);
    if (!match || !ref.current || !('IntersectionObserver' in window)) { setDisplay(value); return undefined; }
    const numStr = match[1];
    const target = parseFloat(numStr.replace(/[\s,]/g, ''));
    if (!isFinite(target) || target === 0) { setDisplay(value); return undefined; }
    const decimals = (numStr.split('.')[1] || '').length;
    const groupSpaces = /\d[\s,]\d/.test(numStr);
    let raf;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || ranRef.current) return;
      ranRef.current = true;
      io.disconnect();
      const start = performance.now();
      const DURATION = 1400;
      const tick = (now) => {
        const p = Math.max(0, Math.min(1, (now - start) / DURATION));  // clamp: no negative first frame
        if (p >= 1) { setDisplay(value); return; }   // snap to the EXACT final value
        const eased = 1 - Math.pow(1 - p, 3);
        let n = (target * eased).toFixed(decimals);
        if (groupSpaces) n = Number(n).toLocaleString('fr-FR');
        setDisplay(String(value).replace(numStr, n));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    io.observe(ref.current);
    // If the effect ever re-runs (or unmounts) mid-animation, never leave a stuck
    // intermediate like "22.1%" on screen — always fall back to the true final value.
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); setDisplay(value); };
  }, [value]);

  return (
    <div ref={ref}>
      <div className="font-display text-3xl md:text-4xl font-bold text-white">{display}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}

const DEMO_CONVS = [
  {
    n: 'Lucas P.', m: 'You: Done, sent 🔥', t: '1m', tags: ['VIP'],
    online: true,
    insight: { 'First name': 'Lucas', Birthday: '08/04/91', Total: '2 140 €', Sales: '18', 'Max sale': '320 €', 'Last sale': '120 €', 'Client since': '92d' },
    notes: 'VIP. Prefers exclusive content. Renews monthly.',
    bubbles: [
      ['left', 'Hey, is the bundle from Saturday still up?', '14:21'],
      ['right', 'Yes 💖 want it sent right now?', '14:22'],
      ['left', "Let's do it. Same as last time, 120 ?", '14:23'],
      ['right', 'Perfect, sending the DropFans link now', '14:24'],
      ['left', "Paid. You're a star ⭐", '14:25'],
    ],
  },
  {
    n: 'Pierre M.', m: 'Will check tonight', t: '14m', tags: [],
    online: false,
    insight: { 'First name': 'Pierre', Birthday: '21/10/87', Total: '380 €', Sales: '4', 'Max sale': '95 €', 'Last sale': '60 €', 'Client since': '14d' },
    notes: 'Polite, slow buyer. Loves tease videos.',
    bubbles: [
      ['left', 'Hey, how are you today?', '13:50'],
      ['right', 'Doing great Pierre! Did you see the new tease?', '13:52'],
      ['left', "Not yet, I'll check tonight after work", '13:55'],
    ],
  },
  {
    n: 'Antoine F.', m: 'Need pricing for premium', t: '47m', tags: ['New'],
    online: true,
    insight: { 'First name': 'Antoine', Birthday: '03/02/95', Total: '0 €', Sales: '0', 'Max sale': '—', 'Last sale': '—', 'Client since': '2d' },
    notes: 'New lead. Asked about premium tier. Hot.',
    bubbles: [
      ['left', "Hi, I'm new here — how does the premium work?", '13:18'],
      ['right', 'Welcome Antoine 👋 there are 3 tiers: Discover 19€, Lover 49€, VIP 99€', '13:21'],
      ['left', 'Need pricing for premium', '13:23'],
    ],
  },
  {
    n: 'Tom B.', m: 'Renewed VIP 🎉', t: '2h', tags: ['VIP'],
    online: false,
    insight: { 'First name': 'Tom', Birthday: '17/07/89', Total: '4 880 €', Sales: '36', 'Max sale': '500 €', 'Last sale': '99 €', 'Client since': '7m' },
    notes: 'Top spender. Long-term VIP. Birthday in July.',
    bubbles: [
      ['right', 'Tom! Your VIP is about to expire — want me to renew?', '12:10'],
      ['left', 'Yes please, you know me 😎', '12:12'],
      ['right', 'Done. Enjoy the next 30 days 💎', '12:13'],
      ['left', 'Renewed VIP 🎉', '12:14'],
    ],
  },
  {
    n: 'Hugo D.', m: 'Maybe later', t: '5h', tags: ['Cool'],
    online: false,
    insight: { 'First name': 'Hugo', Birthday: '11/12/93', Total: '710 €', Sales: '9', 'Max sale': '180 €', 'Last sale': '40 €', 'Client since': '63d' },
    notes: 'Occasional buyer. Responds to time-limited offers.',
    bubbles: [
      ['right', 'Limited drop today only — 50 % off the bundle', '09:30'],
      ['left', 'Maybe later, busy at work today', '09:42'],
    ],
  },
];

function DemoCard() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const c = DEMO_CONVS[active];
  // Mini-bac à sable : le visiteur peut taper et "envoyer" un message pour tester,
  // sans s'inscrire. Le fan répond automatiquement. Tout est local (aucun envoi réel).
  const [draft, setDraft] = useState('');
  const [extra, setExtra] = useState([]);
  useEffect(() => { setExtra([]); }, [active]);
  const REPLIES = ['haha t’es trop toi 🙈', 'oh raconte… 👀', 'tu me manques là', 'montre voir 😏', 'j’adore quand tu m’écris 🤍'];
  const sendDemo = (e) => {
    e.preventDefault();
    const v = draft.trim();
    if (!v) return;
    setPaused(true);
    setExtra(x => [...x, ['right', v, 'maintenant']]);
    setDraft('');
    const reply = REPLIES[extra.length % REPLIES.length];
    setTimeout(() => setExtra(x => [...x, ['left', reply, 'maintenant']]), 850);
  };

  // Auto-rotate the demo conversations so the hero feels alive;
  // stops as soon as the visitor interacts with it.
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setActive(a => (a + 1) % DEMO_CONVS.length), 4500);
    return () => clearInterval(id);
  }, [paused]);
  return (
    <div className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-2 shadow-2xl shadow-indigo-500/10" data-testid="hero-inbox-demo">
      <div className="rounded-2xl bg-[#0B0B0E] border border-white/5 grid grid-cols-12 overflow-hidden min-h-[520px]">
        <div className="col-span-2 border-r border-white/5 p-3 space-y-2">
          <div className="text-xs text-slate-500 px-2 py-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> {t('demo.liveSync')}
          </div>
          {['@Lyon_Hub', '@Paris_VIP', '@Nice_Premium'].map((h, i) => (
            <div key={h} className={`rounded-xl p-2.5 flex items-center gap-2 transition-colors ${i === 0 ? 'bg-indigo-500/15 border border-indigo-500/30' : 'hover:bg-white/5 cursor-pointer border border-transparent'}`}>
              <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${i === 0 ? 'from-indigo-500 to-violet-500' : i === 1 ? 'from-cyan-500 to-blue-500' : 'from-pink-500 to-rose-500'} flex items-center justify-center text-white text-xs font-bold`}>
                {h[1].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-white truncate">{h}</div>
                <div className="text-[10px] text-slate-500">{t('demo.unread', { count: [3, 0, 7][i] })}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-4 border-r border-white/5">
          <div className="p-3 border-b border-white/5">
            <input placeholder={t('demo.search')} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-slate-500 outline-none focus:border-indigo-500/50" />
          </div>
          <div className="divide-y divide-white/5">
            {DEMO_CONVS.map((conv, i) => (
              <button key={conv.n} type="button" onClick={() => { setActive(i); setPaused(true); }} data-testid={`demo-conv-${i}`}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors ${i === active ? 'bg-indigo-500/10 border-l-2 border-indigo-400' : 'hover:bg-white/[0.02] border-l-2 border-transparent'}`}>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-xs font-semibold text-slate-200 shrink-0">
                  {conv.n.split(' ').map(x => x[0]).join('')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-white">{conv.n}</div>
                    <div className="text-[10px] text-slate-500">{conv.t}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">{conv.m}</div>
                  <div className="flex gap-1 mt-1">
                    {conv.tags.map(tg => (
                      <span key={tg} className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">{tg}</span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-4 border-r border-white/5 flex flex-col">
          <div className="p-3 border-b border-white/5 flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center text-xs font-semibold text-white">{c.n.split(' ').map(x => x[0]).join('')}</div>
            <div>
              <div className="text-sm font-semibold text-white">{c.n}</div>
              <div className={`text-[10px] flex items-center gap-1 ${c.online ? 'text-emerald-400' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${c.online ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                {c.online ? t('demo.online') : t('demo.offline')}
              </div>
            </div>
            {c.tags[0] && <Badge className="ml-auto bg-amber-500/15 text-amber-300 border-amber-500/30">{c.tags[0]}</Badge>}
          </div>
          <div className="flex-1 p-3 space-y-2 overflow-y-auto scrollbar-thin" data-testid="demo-chat-bubbles">
            {[...c.bubbles, ...extra].map(([side, text, time], i) => (
              <Bubble key={i} side={side} text={text} time={time} />
            ))}
          </div>
          <form onSubmit={sendDemo} className="p-3 border-t border-white/5 flex items-center gap-2">
            <input value={draft} onChange={e => setDraft(e.target.value)} onFocus={() => setPaused(true)}
              placeholder="Écris un message et teste… (pour de faux 😉)"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500 outline-none focus:border-indigo-500/50" />
            <button type="submit" aria-label="Envoyer" className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0 hover:brightness-110 transition" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>
              <Send size={14} />
            </button>
          </form>
        </div>

        <div className="col-span-2 p-3 space-y-3 text-xs" data-testid="demo-insight-panel">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('demo.insight')}</div>
          {Object.entries(c.insight).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-white/5 pb-1.5">
              <span className="text-slate-500">{k}</span>
              <span className="text-white font-medium">{v}</span>
            </div>
          ))}
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{t('demo.notes')}</div>
            <div className="bg-white/[0.03] border border-white/5 rounded-lg p-2 text-[11px] text-slate-300">{c.notes}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ side, text, time }) {
  const isLeft = side === 'left';
  return (
    <div className={`flex ${isLeft ? 'justify-start' : 'justify-end'}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[11px] ${isLeft ? 'bg-white/5 text-slate-200 rounded-bl-sm' : 'bg-indigo-500 text-white rounded-br-sm'}`}>
        <div>{text}</div>
        <div className={`text-[9px] mt-0.5 ${isLeft ? 'text-slate-500' : 'text-indigo-200/70'}`}>{time}</div>
      </div>
    </div>
  );
}

function Integrations() {
  const { t } = useTranslation();
  const items = t('integrations.items', { returnObjects: true });
  const styles = [
    { gradient: 'from-sky-500/15 via-cyan-500/10 to-transparent', iconColor: 'text-sky-300', Icon: MessageSquare },
    { gradient: 'from-amber-500/15 via-rose-500/10 to-transparent', iconColor: 'text-amber-300', Icon: Crown },
    { gradient: 'from-emerald-500/15 via-teal-500/10 to-transparent', iconColor: 'text-emerald-300', Icon: Globe },
  ];
  return (
    <section id="integrations" className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-14">
        <div className="text-sm text-indigo-400 font-medium mb-3">{t('integrations.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white">{t('integrations.title')}</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-4 stagger">
        {items.map((it, i) => {
          const s = styles[i];
          return (
            <div key={it.tag} className={`rounded-3xl border border-white/10 bg-gradient-to-br ${s.gradient} p-7 hover:-translate-y-1 hover:border-white/20 transition-all duration-300 cursor-default group`}>
              <div className="w-11 h-11 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                <s.Icon size={20} className={s.iconColor} />
              </div>
              <div className="text-[10px] uppercase tracking-wider text-indigo-400 font-semibold mb-2">{it.tag}</div>
              <h3 className="font-display text-2xl font-bold text-white mb-3">{it.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{it.desc}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Scoped landing animations ──
function LandingAnims() {
  return (
    <style>{`
      @keyframes lp-bargrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
      @keyframes lp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
      @keyframes lp-glow { 0%,100% { opacity:.3; } 50% { opacity:1; } }
      @keyframes lp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      @keyframes lp-pop { 0%,48% { opacity:0; transform: translateY(10px) scale(.96); } 60%,90% { opacity:1; transform: translateY(0) scale(1); } 100% { opacity:0; transform: translateY(10px) scale(.96); } }
      @keyframes lp-send { 0%,40% { opacity:0; transform: translateX(14px); } 52%,100% { opacity:1; transform: translateX(0); } }
      @keyframes lp-unlock { 0%,46% { opacity:1; } 58%,100% { opacity:0; } }
      .lp-pop { animation: lp-pop 5s ease-in-out infinite; }
      .lp-send { animation: lp-send 5s ease-in-out infinite; }
      .lp-unlock { animation: lp-unlock 5s ease-in-out infinite; }
      .reveal.visible .lp-bar { transform-origin:left; animation: lp-bargrow 1.1s cubic-bezier(.2,.8,.2,1) both; }
      .lp-float { animation: lp-float 6s ease-in-out infinite; transition: box-shadow .35s ease, border-color .35s ease; }
      .lp-float:hover { box-shadow: 0 0 55px rgba(63,125,255,.22); border-color: rgba(63,125,255,.45) !important; }
      .lp-glow { animation: lp-glow 2.2s ease-in-out infinite; }
      .lp-card { transition: transform .25s ease, border-color .25s ease, background .25s ease; }
      .lp-shine { background: linear-gradient(110deg, transparent 35%, rgba(255,255,255,.05) 50%, transparent 65%); background-size: 200% 100%; animation: lp-shimmer 4s linear infinite; }
    `}</style>
  );
}

// ── Count-up number span (reveal-triggered) ──
function CountUp({ value, className }) {
  const ref = React.useRef(null);
  const ranRef = React.useRef(false);
  const [display, setDisplay] = useState('0');
  useEffect(() => {
    if (ranRef.current) { setDisplay(value); return undefined; }
    const match = String(value).match(/([\d\s,.]*\d)/);
    if (!match || !ref.current || !('IntersectionObserver' in window)) { setDisplay(value); return undefined; }
    const numStr = match[1];
    const target = parseFloat(numStr.replace(/[\s,]/g, ''));
    if (!isFinite(target) || target === 0) { setDisplay(value); return undefined; }
    const groupSpaces = /\d[\s,]\d/.test(numStr);
    let raf;
    const io = new IntersectionObserver((e) => {
      if (!e[0].isIntersecting || ranRef.current) return;
      ranRef.current = true; io.disconnect();
      const start = performance.now(), DUR = 1500;
      const tick = (now) => {
        const p = Math.max(0, Math.min(1, (now - start) / DUR));
        if (p >= 1) { setDisplay(value); return; }
        const eased = 1 - Math.pow(1 - p, 3);
        let n = Math.round(target * eased);
        if (groupSpaces) n = n.toLocaleString('fr-FR');
        setDisplay(String(value).replace(numStr, String(n)));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    io.observe(ref.current);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); setDisplay(value); };
  }, [value]);
  return <span ref={ref} className={className}>{display}</span>;
}

// ── "Everything included" feature wall ──
function Everything() {
  const tools = [
    { Icon: FolderOpen, t: 'Vault média', d: 'Bibliothèque PPV partagée, anti-doublon par fan.', grad: 'from-pink-500/15', ic: 'text-pink-300', bd: 'hover:border-pink-500/40' },
    { Icon: MessageSquare, t: 'Campagnes de masse', d: 'Relance les inactifs en 1 clic, sans te faire bannir.', grad: 'from-sky-500/15', ic: 'text-sky-300', bd: 'hover:border-sky-500/40' },
    { Icon: Sparkles, t: 'Smart lists', d: 'VIP, gros dépensiers, sans réponse — segmentés tout seuls.', grad: 'from-amber-500/15', ic: 'text-amber-300', bd: 'hover:border-amber-500/40' },
    { Icon: Crown, t: 'Intégration DropFans', d: 'Envoie le contenu payant + importe les ventes auto.', grad: 'from-fuchsia-500/15', ic: 'text-fuchsia-300', bd: 'hover:border-fuchsia-500/40' },
    { Icon: Zap, t: 'Transcription vocale', d: 'Les vocaux des fans transcrits, comme Telegram Premium.', grad: 'from-cyan-500/15', ic: 'text-cyan-300', bd: 'hover:border-cyan-500/40' },
    { Icon: Users, t: 'Planning équipe', d: 'Shifts, dispos des chatteurs, planning récurrent.', grad: 'from-emerald-500/15', ic: 'text-emerald-300', bd: 'hover:border-emerald-500/40' },
    { Icon: BarChart3, t: 'Journal des messages', d: 'Qui a écrit quoi, à qui, quand — en temps réel.', grad: 'from-violet-500/15', ic: 'text-violet-300', bd: 'hover:border-violet-500/40' },
    { Icon: Globe, t: 'Traduction intégrée', d: 'Réponds à un fan dans sa langue, d’un seul clic.', grad: 'from-rose-500/15', ic: 'text-rose-300', bd: 'hover:border-rose-500/40' },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-12 text-center max-w-2xl mx-auto">
        <div className="text-sm text-indigo-400 font-medium mb-3">Et tout le reste</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white">Tout est déjà dedans.</h2>
        <p className="text-slate-400 mt-4">Pas d&apos;add-ons, pas de surprise. Chaque outil dont une agence a besoin, inclus.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {tools.map(it => (
          <div key={it.t} className={`lp-card group rounded-2xl border border-white/10 bg-gradient-to-br ${it.grad} to-transparent ${it.bd} p-5`}>
            <div className="w-11 h-11 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center mb-3.5 group-hover:scale-110 group-hover:-rotate-6 transition-transform">
              <it.Icon size={19} className={it.ic} />
            </div>
            <div className="text-[15px] font-semibold text-white mb-1">{it.t}</div>
            <div className="text-[13px] text-slate-400 leading-relaxed">{it.d}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── THE PROBLEM — why running an OFM agency on phones/Telegram is chaos ──
function Problem() {
  const points = [
    { Icon: Users, t: 'Tes modèles partout, ton attention nulle part', d: 'Plusieurs comptes, des dizaines de fans par modèle, et toi qui jongles entre téléphones et captures. Tu perds le fil — et des ventes avec.' },
    { Icon: FolderOpen, t: 'Tu retapes au lieu de vendre', d: 'Sans vault ni scripts centralisés, tes chatteurs cherchent le bon média et recopient les mêmes phrases. Chaque seconde perdue refroidit le fan.' },
    { Icon: Flame, t: 'Tes gros dépensiers s’éteignent', d: 'Sans relance ni suivi des baleines, tes meilleurs payeurs refroidissent et disparaissent. Tu remplis un seau percé : tu cours après de nouveaux fans au lieu de grossir.' },
    { Icon: BarChart3, t: 'Le Google Sheet ne suit plus', d: 'À 5 chatteurs et 3 modèles, le suivi manuel explose : qui a vendu quoi, à qui, pour combien ? Plus personne ne sait vraiment. Impossible de décider.' },
  ];
  return (
    <section className="max-w-6xl mx-auto px-6 py-24 reveal">
      <div className="max-w-3xl mb-12">
        <div className="text-sm text-rose-400 font-medium mb-3">Pourquoi tu plafonnes</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">
          Ton agence a un plafond,<br /><span className="text-slate-500">et tu l&apos;as déjà touché.</span>
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {points.map(p => (
          <div key={p.t} className="rounded-2xl border border-rose-500/15 bg-rose-500/[0.03] p-6">
            <div className="w-11 h-11 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-4">
              <p.Icon size={20} className="text-rose-300" />
            </div>
            <div className="text-base font-semibold text-white mb-1.5">{p.t}</div>
            <div className="text-sm text-slate-400 leading-relaxed">{p.d}</div>
          </div>
        ))}
      </div>
      <p className="text-center text-lg text-slate-300 mt-12 max-w-2xl mx-auto">
        Sans un outil qui centralise tout et automatise le reste, passer à l&apos;échelle n&apos;est pas difficile — <span className="text-white font-semibold">c&apos;est mathématiquement impossible.</span>
      </p>
    </section>
  );
}

// ── COMPARATIF — ce que ChatHub fait que les CRM génériques ne font pas ──
function Comparison() {
  const rows = [
    ['Connexion Telegram comme l’app officielle (pas un bot fragile)', true, false],
    ['IP mobile dédiée par compte → tes comptes ne se font pas bannir', true, false],
    ['Chaque vente DropFans attribuée automatiquement au bon chatteur', true, false],
    ['Assistant IA de chatting, adapté à chaque modèle, dans l’inbox', true, false],
    ['Vault média + scripts + Mass DM réunis au même endroit', true, 'partiel'],
    ['CA en temps réel par chatteur, par modèle et par fan', true, 'partiel'],
    ['Relance des gros dépensiers en un clic', true, false],
    ['Pensé, parlé et supporté en français', true, false],
  ];
  const Cell = ({ v }) => v === true
    ? <span className="inline-flex w-7 h-7 rounded-full bg-emerald-500/15 items-center justify-center"><Check size={16} className="text-emerald-400" /></span>
    : v === 'partiel'
      ? <span className="text-[11px] text-amber-300/90 font-medium">partiel</span>
      : <span className="inline-flex w-7 h-7 rounded-full bg-white/[0.04] items-center justify-center"><X size={15} className="text-slate-600" /></span>;
  return (
    <section id="comparatif" className="max-w-5xl mx-auto px-6 py-24 reveal">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="text-sm text-indigo-400 font-medium mb-3">Pourquoi ChatHub</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">Ce que les autres outils ne font pas.</h2>
        <p className="text-slate-400 mt-4">Les CRM génériques gèrent des messages. ChatHub gère ton business : du premier DM jusqu’à l’euro encaissé.</p>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="grid grid-cols-[1fr_110px_110px] items-center px-5 sm:px-7 py-4 border-b border-white/10 bg-white/[0.02]">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Fonctionnalité</div>
          <div className="text-center text-sm font-bold text-white">ChatHub</div>
          <div className="text-center text-xs text-slate-500 font-medium">Les autres</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_110px_110px] items-center px-5 sm:px-7 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.015] transition-colors">
            <div className="text-[13px] sm:text-sm text-slate-200 pr-3 leading-snug">{r[0]}</div>
            <div className="flex justify-center"><Cell v={r[1]} /></div>
            <div className="flex justify-center"><Cell v={r[2]} /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── SPOTLIGHTS — feature scenes, money first, each with a real mockup ──
function Spotlights() {
  const scenes = [
    { tag: 'Comptes & contacts', title: 'Tous tes comptes, réunis.', desc: 'Connecte chaque compte Telegram en scannant un QR, comme sur ton téléphone. Tout l’historique remonte, et tu passes d’un modèle à l’autre dans une seule fenêtre.', bullets: ['Connexion par QR, 2 min par compte', 'Multi-comptes, multi-modèles', 'Tout l’historique récupéré automatiquement'], Visual: InboxVisual },
    { tag: 'Vault & scripts', title: 'Le bon média, en 2 clics.', desc: 'Tes photos, vidéos et vocaux rangés par modèle. Tes meilleures phrases en scripts. Tape « / », choisis, envoie : fini de fouiller et de tout retaper.', bullets: ['Vault média trié par modèle', 'Scripts insérés en « / »', 'Plus de copier-coller, plus d’attente'], Visual: SpeedVisual },
    { tag: 'Paiement', title: 'Ton processeur intégré.', desc: 'Encaisse directement depuis la conversation avec DropFans. Notification de vente en temps réel, et chaque euro attribué tout seul au bon chatteur et au bon modèle.', bullets: ['Paiement DropFans dans le CRM', 'Notification de vente en temps réel', 'Attribution automatique de chaque vente'], Visual: PpvVisual },
    { tag: 'Suivi', title: 'Chaque vente, suivie toute seule.', desc: 'Le CA par chatteur, par modèle, par fan, sans aucun Google Sheet. Tu vois qui performe, qui décroche et où part l’argent, en temps réel.', bullets: ['Performance par chatteur et par modèle', 'CA et LTV par fan, en direct', 'Tableau de bord temps réel'], Visual: MoneyVisual },
    { tag: 'Mass DM', title: 'Relance toute une liste d’un coup.', desc: 'Filtre tes fans par statut, groupe ou tag, écris un message avec des variables, et lance la campagne. Chaque envoi tracé, à une cadence qui protège tes comptes.', bullets: ['Ciblage par statut, groupe ou tag', 'Variables dynamiques (prénom, etc.)', 'Suivi par campagne, cadence anti-ban'], Visual: MassDmVisual },
    { tag: 'Assistant IA', title: 'Une réponse prête, par modèle.', desc: 'En plus : un assistant IA dans l’inbox. Choisis le mode (relationnel, vente, relance…), il lit la conversation et propose une réponse fidèle au modèle. Tu cliques, c’est inséré.', bullets: ['Suggestions adaptées à chaque modèle', '5 modes : relationnel, vente, relance, négociation…', 'Clé IA protégée côté serveur'], Visual: AiVisual, mine: true },
  ];
  return (
    <section id="features" className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-16 max-w-3xl">
        <div className="text-sm text-indigo-400 font-medium mb-3">La solution</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">Ce que ChatHub change.</h2>
        <p className="text-slate-400 mt-4 text-lg leading-relaxed">De la connexion d&apos;un compte jusqu&apos;à l&apos;euro encaissé, chaque étape s&apos;enchaîne dans une seule app. Voilà le parcours.</p>
      </div>
      <div className="space-y-20">
        {scenes.map((s, i) => (
          <div key={s.title}>
            <div className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${i % 2 ? 'md:[&>*:first-child]:order-2' : ''}`}>
              <div>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="font-display text-3xl font-bold text-indigo-400/80 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <span className="h-px w-10 bg-gradient-to-r from-indigo-500/50 to-transparent" />
                  <span className="text-xs uppercase tracking-wider text-indigo-400 font-semibold">{s.tag}</span>
                  {s.mine && <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/25 font-medium">+ chez ChatHub</span>}
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">{s.title}</h3>
                <p className="text-slate-400 mb-6 leading-relaxed text-lg">{s.desc}</p>
                <ul className="space-y-2.5">
                  {s.bullets.map(b => (
                    <li key={b} className="flex items-start gap-2.5 text-[15px] text-slate-200">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center mt-0.5 shrink-0"><Check size={12} className="text-emerald-300" /></span>{b}
                    </li>
                  ))}
                </ul>
              </div>
              <s.Visual />
            </div>
            {i < scenes.length - 1 && (
              <div className="flex justify-center pt-12" aria-hidden="true">
                <div className="w-px h-12 bg-gradient-to-b from-indigo-500/40 to-transparent" />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function PpvVisual() {
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-4 shadow-2xl shadow-emerald-500/10 lp-float">
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-[11px] text-white font-bold">M</div>
        <div className="text-xs text-white font-medium">Maya <span className="text-slate-500 font-normal">· conversation fan</span></div>
        <span className="ml-auto w-2 h-2 rounded-full bg-emerald-400 lp-glow" />
      </div>
      <div className="flex mb-2"><div className="max-w-[75%] rounded-2xl rounded-bl-md bg-white/[0.05] px-3 py-2 text-[12px] text-slate-200">t&apos;as quoi pour moi bb ? 😏</div></div>
      <div className="flex justify-end mb-1">
        <div className="lp-send max-w-[80%] rounded-2xl rounded-br-md overflow-hidden border border-indigo-500/30 bg-indigo-500/10">
          <div className="relative h-24 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(63,125,255,.35), rgba(217,70,239,.18), rgba(63,125,255,.10))' }}>
            <div className="absolute inset-0 backdrop-blur-md" />
            <div className="lp-unlock relative flex flex-col items-center gap-1 text-white">
              <Lock size={18} />
              <span className="text-[11px] font-semibold">Contenu verrouillé · 19 €</span>
            </div>
          </div>
          <div className="px-3 py-1.5 text-[11px] text-indigo-200 flex items-center justify-between">
            <span>PPV envoyé</span><span className="text-slate-400">23:51 ✓✓</span>
          </div>
        </div>
      </div>
      <div className="lp-pop mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0"><Zap size={14} className="text-emerald-300" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-white font-semibold">Vente DropFans encaissée</div>
          <div className="text-[10px] text-emerald-300/80">attribuée à Léa · modèle Maya</div>
        </div>
        <span className="text-emerald-300 font-bold text-sm shrink-0">+19 €</span>
      </div>
    </div>
  );
}

function MassDmVisual() {
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-indigo-500/5 lp-float">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Campagne de relance</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {['VIP', 'Inactifs 7j', 'Gros dépensiers'].map(t => (
          <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">{t}</span>
        ))}
      </div>
      <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3 text-[13px] text-slate-200 mb-3 leading-relaxed">
        coucou <span className="text-indigo-300">{'{prénom}'}</span> 🙈 j&apos;ai une surprise rien que pour toi…
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">312 fans ciblés</span>
        <span className="inline-flex items-center gap-1.5 text-white font-medium px-3 py-1.5 rounded-lg" style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>Envoyer la campagne</span>
      </div>
    </div>
  );
}

function AiVisual() {
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-indigo-500/10 lp-float">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-3">
        <Sparkles size={12} className="text-indigo-300" /> Assistant IA · Maya
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {['Relationnel', 'Vente', 'Relance', 'Négociation'].map((m, k) => (
          <span key={m} className={`text-[11px] px-2 py-0.5 rounded-full border ${k === 0 ? 'bg-indigo-500/25 text-white border-indigo-500/50' : 'bg-white/[0.03] text-slate-400 border-white/10'}`}>{m}</span>
        ))}
      </div>
      <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/20 p-3 text-[13px] text-slate-100 leading-relaxed">
        raconte-moi, c&apos;était quoi le meilleur moment de ta journée ? 🥰
      </div>
      <div className="text-right mt-2 text-[11px] text-indigo-300 font-medium">Insérer dans la réponse →</div>
    </div>
  );
}

function MoneyVisual() {
  const chatters = [['Léa', '4 280 €', '92%'], ['Marco', '3 110 €', '68%'], ['Sofia', '2 540 €', '55%'], ['Tom', '1 190 €', '28%']];
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-emerald-500/10 lp-float">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 lp-glow" /> CA ce mois · temps réel
        </div>
        <div className="text-emerald-300 text-sm font-semibold flex items-center gap-1"><BarChart3 size={14} /> +23%</div>
      </div>
      <CountUp value="11 120 €" className="font-display text-3xl font-bold text-white block mb-4" />
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2.5">Top chatteurs</div>
      <div className="space-y-2.5">
        {chatters.map(([n, v, w]) => (
          <div key={n} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-[11px] text-white font-bold shrink-0">{n[0]}</div>
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1"><span className="text-white">{n}</span><span className="text-emerald-300 font-medium">{v}</span></div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="lp-bar h-full rounded-full bg-gradient-to-r from-[#3f7dff] to-[#73a6ff]" style={{ width: w }} /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackingVisual() {
  const rows = [['Instagram bio', '312', '58%', '1 840 €'], ['TikTok #3', '204', '41%', '720 €'], ['Reddit promo', '96', '22%', '210 €']];
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-indigo-500/5">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Performance par source</div>
      <div className="grid grid-cols-4 text-[10px] uppercase tracking-wider text-slate-600 pb-2 border-b border-white/5">
        <span>Source</span><span className="text-right">Joins</span><span className="text-right">DM</span><span className="text-right">Revenu</span>
      </div>
      {rows.map(([s, j, d, r]) => (
        <div key={s} className="grid grid-cols-4 py-2.5 text-xs items-center border-b border-white/5 last:border-0">
          <span className="text-white font-medium truncate">{s}</span>
          <span className="text-right text-slate-300">{j}</span>
          <span className="text-right text-indigo-300">{d}</span>
          <span className="text-right text-emerald-300 font-semibold">{r}</span>
        </div>
      ))}
      <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5"><Sparkles size={12} className="text-amber-300" /> Instagram rapporte 2,5× plus que Reddit.</div>
    </div>
  );
}

function SpeedVisual() {
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-indigo-500/5">
      <div className="rounded-xl border border-white/10 overflow-hidden mb-3">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">/ Scripts — Entrée = envoi</div>
        {[['Accroche', 'salut prénom 😏', '#38bdf8'], ['Vente', 'PPV 19€ 📎', '#34d399'], ['Relance', 'tu es là ? 👀', '#f59e0b']].map(([tag, txt, c], i) => (
          <div key={txt} className={`flex items-center gap-2 px-3 py-2.5 ${i === 0 ? 'bg-indigo-500/15' : ''}`}>
            <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${c}26`, color: c }}>{tag}</span>
            <span className="text-xs text-white truncate">{txt}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-3 py-2.5">
        <span className="text-sm text-indigo-300 flex-1 font-mono">/accroche|</span>
        <div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center"><Zap size={13} className="text-white" /></div>
      </div>
    </div>
  );
}

function InboxVisual() {
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-indigo-500/5 space-y-2">
      {[['Emma', 'from-indigo-500 to-violet-500', 3], ['Mia', 'from-cyan-500 to-blue-500', 0], ['Luna', 'from-pink-500 to-rose-500', 7]].map(([n, g, u]) => (
        <div key={n} className="flex items-center gap-3 rounded-xl p-2.5 bg-white/[0.03] border border-white/5">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${g} flex items-center justify-center text-xs text-white font-bold`}>{n[0]}</div>
          <div className="flex-1 text-sm text-white">{n}</div>
          {u > 0 && <span className="text-[10px] bg-rose-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold">{u}</span>}
        </div>
      ))}
      <div className="flex flex-wrap gap-1.5 pt-2">
        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">👑 VIP · 24</Badge>
        <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-[10px]">🔥 Gros dépensiers · 31</Badge>
      </div>
    </div>
  );
}

function TeamVisual() {
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/10 p-5 shadow-2xl shadow-indigo-500/5 space-y-3">
      {[['Léa', 'Chatteuse', 'Emma, Mia'], ['Marco', 'Manager', 'Tous les comptes'], ['Sofia', 'Chatteuse', 'Luna']].map(([n, role, acc]) => (
        <div key={n} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-[11px] text-white font-bold">{n[0]}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white">{n} <span className="text-[10px] text-indigo-300 ml-1">{role}</span></div>
            <div className="text-[11px] text-slate-500 truncate">Accès : {acc}</div>
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/20 px-3 py-2.5 mt-1">
        <Shield size={15} className="text-emerald-300 shrink-0" />
        <span className="text-[11px] text-emerald-200">Chaque compte sur son IP dédiée — protégé du bannissement.</span>
      </div>
    </div>
  );
}

function Workflow() {
  const { t } = useTranslation();
  const blocks = t('workflow.blocks', { returnObjects: true });
  return (
    <section id="workflow" className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-16 text-center">
        <div className="text-sm text-indigo-400 font-medium mb-3">{t('workflow.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white max-w-3xl mx-auto">{t('workflow.title')}</h2>
      </div>
      <div className="space-y-6">
        {blocks.map((b, i) => (
          <div key={b.title} className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-wider text-indigo-400 font-semibold mb-3">{b.tag}</div>
              <h3 className="font-display text-3xl font-bold text-white mb-4">{b.title}</h3>
              <p className="text-slate-400 mb-6 leading-relaxed">{b.desc}</p>
              <ul className="space-y-2">
                {b.bullets.map(x => (
                  <li key={x} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {x}
                  </li>
                ))}
              </ul>
            </div>
            <WorkflowVisual index={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

function WorkflowVisual({ index }) {
  if (index === 0) {
    return (
      <div className="rounded-2xl bg-[#0B0B0E] border border-white/5 p-4">
        <div className="rounded-xl border border-white/10 overflow-hidden mb-2.5">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">/ Scripts · Entrée = envoi</div>
          {[
            { tag: 'Accroche', t: '01 · msg prénom', c: '#38bdf8' },
            { tag: 'Vente', t: 'PPV 19€  📎', c: '#34d399' },
            { tag: 'Relance', t: 'tu es là ? 👀', c: '#f59e0b' },
          ].map((s, i) => (
            <div key={s.t} className={`flex items-center gap-2 px-3 py-2 ${i === 0 ? 'bg-indigo-500/15' : ''}`}>
              <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: `${s.c}26`, color: s.c }}>{s.tag}</span>
              <span className="text-xs text-white truncate">{s.t}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
          <span className="text-sm text-indigo-300 flex-1 font-mono">/</span>
          <div className="w-7 h-7 rounded-lg bg-indigo-500 flex items-center justify-center"><Zap size={13} className="text-white" /></div>
        </div>
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="rounded-2xl bg-[#0B0B0E] border border-white/5 p-4">
        <div className="flex gap-2 mb-3">
          {['All', 'Box 1', 'Box 2', 'Box 3'].map((t, i) => (
            <div key={t} className={`text-xs px-3 py-1.5 rounded-lg ${i === 0 ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-400'}`}>{t}</div>
          ))}
        </div>
        {['Sarah K.', 'Mike R.', 'Emma L.', 'James T.'].map((n, i) => (
          <div key={n} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-xs text-white font-semibold">{n[0]}</div>
            <div className="flex-1 text-sm text-white">{n}</div>
            <div className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-slate-400">Box {(i % 3) + 1}</div>
          </div>
        ))}
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="rounded-2xl bg-[#0B0B0E] border border-white/5 p-4 space-y-2">
        {[
          { e: <Crown size={14} />, n: 'VIP Clients', c: 24, r: 'Total spent > 200 €' },
          { e: <MessageSquare size={14} />, n: 'Inactive 7d+', c: 12, r: 'Last message > 7 days' },
          { e: <Sparkles size={14} />, n: 'New Clients', c: 8, r: 'Client since < 3 days' },
          { e: <Flame size={14} />, n: 'High Potential', c: 31, r: 'Spent 50€–200€' },
        ].map((l, i) => (
          <div key={l.n} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/15 text-indigo-300 flex items-center justify-center">{l.e}</div>
            <div className="flex-1">
              <div className="text-sm font-medium text-white">{l.n}</div>
              <div className="text-[11px] text-slate-500">{l.r}</div>
            </div>
            <div className="text-sm font-bold text-indigo-300">{l.c}</div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-[#0B0B0E] border border-white/5 p-5 space-y-2 text-sm">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Insight</div>
      {[['First name', 'Sarah'], ['Birthday', '15/03/1992'], ['Total spent', '1,247 €'], ['Max sale', '200 €'], ['Last sale', '85 €'], ['Client since', '47 days'], ['Last msg', '0 days']].map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-white/5 pb-2">
          <span className="text-slate-500">{k}</span><span className="text-white font-medium">{v}</span>
        </div>
      ))}
      <div className="flex gap-2 pt-3">
        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">👑 VIP</Badge>
        <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30">🔥 High Potential</Badge>
      </div>
    </div>
  );
}

const PAIN_ICONS = { shield: Shield, lock: Lock, eye: Eye, euro: Euro };

function Pains() {
  const { t } = useTranslation();
  const rows = t('pains.rows', { returnObjects: true });
  return (
    <section className="max-w-6xl mx-auto px-6 py-24 reveal">
      <div className="mb-14 text-center max-w-3xl mx-auto">
        <div className="text-sm text-rose-400 font-medium mb-3">{t('pains.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">{t('pains.title')}</h2>
        <p className="text-slate-400 mt-5 leading-relaxed">{t('pains.sub')}</p>
      </div>
      <div className="space-y-4 stagger">
        {(Array.isArray(rows) ? rows : []).map((r) => {
          const Ic = PAIN_ICONS[r.icon] || Shield;
          return (
            <div key={r.topic} className="group rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-white/20 transition-colors">
              <div className="flex items-center gap-3 px-6 md:px-8 pt-6 pb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/10 border border-white/10 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Ic size={18} className="text-indigo-300" />
                </div>
                <h3 className="text-lg font-semibold text-white">{r.topic}</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-px bg-white/5 mt-3">
                <div className="bg-[#0d0f15] px-6 md:px-8 py-5">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-rose-400/90 font-semibold mb-2">
                    <span className="w-5 h-5 rounded-full bg-rose-500/15 flex items-center justify-center"><X size={11} className="text-rose-400" /></span>
                    {t('pains.withoutLabel')}
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{r.without}</p>
                </div>
                <div className="bg-emerald-500/[0.04] px-6 md:px-8 py-5 relative">
                  <div className="absolute inset-y-0 left-0 w-0.5 bg-gradient-to-b from-emerald-400 to-teal-400" />
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-300 font-semibold mb-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center"><Check size={11} className="text-emerald-300" /></span>
                    {t('pains.withLabel')}
                  </div>
                  <p className="text-sm text-slate-200 leading-relaxed">{r.with}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Shift() {
  const { t } = useTranslation();
  const items = t('shift.items', { returnObjects: true });
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-12">
        <div className="text-sm text-emerald-400 font-medium mb-3">{t('shift.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white">{t('shift.title')}</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {items.map((it) => (
          <div key={it.t} className="rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/[0.04] to-transparent p-6">
            <div className="flex items-baseline gap-2 mb-4">
              <div className="font-display text-4xl font-bold text-emerald-400">{it.k}</div>
              <div className="text-xs text-slate-500">{it.l}</div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{it.t}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{it.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const steps = t('how.steps', { returnObjects: true });
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-12">
        <div className="text-sm text-indigo-400 font-medium mb-3">{t('how.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white">{t('how.title')}</h2>
        <p className="text-slate-400 mt-4">{t('how.sub')}</p>
      </div>
      <div className="grid md:grid-cols-4 gap-4 stagger">
        {steps.map(s => (
          <div key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="font-display text-3xl font-bold text-indigo-400 mb-3">{s.n}</div>
            <h3 className="text-lg font-semibold text-white mb-2">{s.t}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Security() {
  const items = [
    { Icon: Euro, t: '0 % de commission côté agence', d: 'Avec DropFans, les frais sont pris côté fan — jamais sur toi. Tu encaisses 100 % de ton prix, même quand tu montes en volume.' },
    { Icon: Users, t: 'Tes chatteurs ne sont pas perdus', d: 'Une interface proche de ce qu’ils connaissent déjà. Zéro temps de formation : ils sont productifs dès le premier jour.' },
    { Icon: Inbox, t: 'Tout l’off-platform réuni', d: 'Telegram et DropFans dans un seul CRM. Fini de jongler entre trois outils et dix onglets ouverts.' },
    { Icon: Shield, t: 'Sécurisé et repris en main', d: 'Sessions chiffrées, IP dédiée par compte, et on récupère ton historique, tes médias et tes scripts à l’installation.' },
  ];
  return (
    <section id="security" className="max-w-6xl mx-auto px-6 py-24 reveal">
      <div className="mb-12 text-center max-w-2xl mx-auto">
        <div className="text-sm text-indigo-400 font-medium mb-3">Le positionnement</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">L&apos;Infloww de Telegram.<br /><span className="text-slate-500">Sans la commission.</span></h2>
        <p className="text-slate-400 mt-4">Tes chatteurs gardent leurs repères, et tu gardes 100 % de tes ventes.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="relative flex items-center justify-center py-10 min-h-[260px]">
          <div className="absolute w-60 h-60 rounded-full border border-indigo-500/15 lp-glow" />
          <div className="absolute w-44 h-44 rounded-full border border-indigo-500/25" />
          <div className="absolute w-28 h-28 rounded-full bg-indigo-500/10 blur-2xl" />
          <div className="relative w-32 h-32 rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-600 flex flex-col items-center justify-center shadow-2xl shadow-indigo-500/30 lp-float">
            <span className="font-display text-5xl font-extrabold text-white leading-none">0%</span>
            <span className="text-[10px] uppercase tracking-wider text-white/80 mt-1.5">commission</span>
          </div>
        </div>
        <div className="divide-y divide-white/5">
          {items.map((it) => (
            <div key={it.t} className="flex gap-4 py-5 group">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0 group-hover:bg-indigo-500/20 transition-colors">
                <it.Icon size={18} className="text-indigo-300" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white mb-1">{it.t}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{it.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || 'en').startsWith('fr') ? 'fr-FR' : 'en-US';
  const fr = (i18n.resolvedLanguage || 'en').startsWith('fr');
  // 25/09 : on peut payer au mois, par 3 mois (-10 %) ou a l'annee (-20 %).
  const [rythme, setRythme] = React.useState(RYTHMES[0]);
  // 25/09 : les prix de la grille sont ronds (69, 109, 249, 449) — « 69 € » et non « 69,00 € ».
  // 69 € reste « 69 € », mais 62,10 € garde ses deux decimales (sinon on lit « 62,1 € »).
  const formatPrice = (price) => price.toLocaleString(locale, {
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2, maximumFractionDigits: 2 });
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 reveal">
      <div className="mb-12 text-center">
        <div className="text-sm text-indigo-400 font-medium mb-3">{t('pricing.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white">{t('pricing.title')}</h2>
        <p className="text-slate-400 mt-4 max-w-xl mx-auto">{t('pricing.sub')}</p>
      </div>
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-1" data-testid="pricing-rythmes">
          {RYTHMES.map((r) => (
            <button key={r.key} type="button" onClick={() => setRythme(r)}
              data-testid={`pricing-rythme-${r.key}`}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                rythme.key === r.key ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'}`}>
              {fr ? r.fr : r.en}
              {r.remise > 0 && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500/20 text-emerald-300">
                  -{Math.round(r.remise * 100)}%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((p, i) => (
          <div key={p.id}
            style={{ animationDelay: `${i * 70}ms` }}
            className={`fade-in-up relative rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1.5 ${
              p.popular
                ? 'border-indigo-500/60 bg-gradient-to-b from-indigo-500/10 to-transparent shadow-xl shadow-indigo-500/20 hover:shadow-2xl hover:shadow-indigo-500/30'
                : 'border-white/10 bg-white/[0.02] hover:border-white/20'
            }`}
            data-testid={`plan-${p.id}`}>
            {p.popular && (
              <Badge className="absolute -top-3 left-7 bg-indigo-500 text-white border-0 shadow-lg shadow-indigo-500/40">{t('pricing.popular')}</Badge>
            )}
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t('pricing.plan')}</div>
            <div className="text-2xl font-bold text-white mb-1">{p.name}</div>
            {p.trial && <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider mb-3" data-testid={`plan-${p.id}-trial`}>{p.trial}</div>}
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-display text-4xl font-bold text-white" data-testid={`plan-${p.id}-price`}>
                {formatPrice(prixPeriode(p, rythme) / rythme.mois)}€
              </span>
              <span className="text-sm text-slate-500">{t('pricing.perMonth')}</span>
            </div>
            <div className="text-[11px] text-slate-500 mb-5 h-4" data-testid={`plan-${p.id}-total`}>
              {rythme.mois > 1
                ? (fr ? `soit ${formatPrice(prixPeriode(p, rythme))} € ${rythme.mois === 12 ? 'par an' : 'tous les 3 mois'}`
                      : `${formatPrice(prixPeriode(p, rythme))} € ${rythme.mois === 12 ? 'per year' : 'every 3 months'}`)
                : ''}
            </div>
            <ul className="space-y-2.5 mb-6 min-h-[120px]">
              <li className="flex items-center gap-2.5 text-sm text-slate-200">
                <Check size={16} className="text-emerald-400 shrink-0" />
                <span><span className="text-white font-bold">{p.accounts}</span> {p.accounts > 1 ? t('pricing.tgAccounts') : t('pricing.tgAccount')}</span>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-slate-200">
                <Check size={16} className="text-emerald-400 shrink-0" />
                <span><span className="text-white font-bold">{p.employees === null ? t('pricing.unlimited') : p.employees}</span> {t('pricing.employeesMax')}</span>
              </li>
              {p.privateWhatsapp && (
                <li className="flex items-center gap-2.5 text-sm text-slate-200">
                  <Check size={16} className="text-emerald-400 shrink-0" />
                  <span className="text-emerald-300 font-medium">{t('pricing.privateWhatsapp')}</span>
                </li>
              )}
            </ul>
            <Link to={`/subscribe?plan=${p.id}${rythme.key === 'quarter' ? '_quarterly' : rythme.key === 'year' ? '_yearly' : ''}`}>
              <Button data-testid={`plan-${p.id}-cta`} className={`w-full font-medium transition-all hover:scale-[1.03] ${
                p.popular
                  ? 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                  : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
              }`}>
                {p.trial ? t('pricing.ctaTrial') : t('pricing.ctaGet')}
              </Button>
            </Link>
          </div>
        ))}
      </div>
      <FeatureTable fr={(i18n.resolvedLanguage || 'en').startsWith('fr')} />
    </section>
  );
}

// Le tableau « ce que chaque plan donne » (grille du 21/09/2026). Il lit la MÊME
// FEATURE_MATRIX que la garde serveur : impossible d'annoncer ici une option qu'un
// plan n'a pas réellement.
function FeatureTable({ fr }) {
  return (
    <div className="mt-14 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]" data-testid="feature-matrix">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left font-medium text-slate-400 p-4 w-[38%]">
              {fr ? 'Ce que chaque plan donne' : 'What each plan includes'}
            </th>
            {PLANS.map((p) => (
              <th key={p.id} className={`p-4 font-display font-semibold text-white ${p.popular ? 'bg-indigo-500/10' : ''}`}>
                {p.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {FEATURE_MATRIX.map((g) => (
            <React.Fragment key={g.group.en}>
              <tr>
                <td colSpan={PLANS.length + 1}
                  className="px-4 py-2 text-[11px] uppercase tracking-[0.1em] font-semibold text-slate-500 bg-white/[0.02]">
                  {fr ? g.group.fr : g.group.en}
                </td>
              </tr>
              {g.rows.map((row) => (
                <tr key={row.en} className="border-t border-white/5">
                  <td className="p-4 text-slate-200">{fr ? row.fr : row.en}</td>
                  {PLANS.map((p) => (
                    <td key={p.id} className={`p-4 text-center ${p.popular ? 'bg-indigo-500/[0.06]' : ''}`}
                      data-testid={`matrix-${row.key || row.en.slice(0, 12)}-${p.id}`}>
                      {row.plans.includes(p.id)
                        ? <Check size={16} className="text-emerald-400 inline" aria-label={fr ? 'inclus' : 'included'} />
                        : <span className="text-slate-600 font-semibold" aria-label={fr ? 'non inclus' : 'not included'}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FAQ_KEY_BY_LANG = {
  en: [
    { q: 'What is ChatHub?', a: 'A CRM built specifically for chatting agencies managing multiple Telegram accounts in real-time, with native DropFans integration for paid-media sales attribution.' },
    { q: 'How does the real Telegram connection work?', a: 'You add your phone number, validate with the SMS code (and 2FA if you have it), and ChatHub speaks Telegram exactly like the official iOS app would. Each account is routed through its own mobile proxy so it always appears to come from the same device.' },
    { q: 'How many Telegram accounts can I connect?', a: 'As many as your plan allows. All conversations from all accounts appear in a single unified inbox, synchronised in real-time.' },
    { q: 'Can I control what my chatters see and do?', a: 'Yes. Our RBAC system lets you create custom roles with 39 granular permissions across 12 domains. Every action is verified server-side and logged.' },
    { q: 'Does ChatHub work with DropFans?', a: 'Yes — fully. Plug in your own DropFans API key + webhook secret, and every paid sale is automatically attributed to the chatter who sent the link first. ChatHub is an independent product and is not affiliated with DropFans.' },
    { q: 'How is media stored? Do I need to upload it every time?', a: 'No. The first time a media is sent, Telegram returns a permanent file-id that ChatHub caches per account. Every subsequent send re-uses that file-id — zero re-upload, zero bandwidth fees, even for a library of thousands of media.' },
    { q: 'Is there a free trial?', a: 'No. You subscribe and the tool is yours right away — no trial to convert, no surprise on day three. Change plan or cancel at any time from your account.' },
    { q: 'How does payment work?', a: 'Credit card. Invoices are generated and tracked automatically, and you can change plan or cancel at any time from your account.' },
    { q: 'Is my data secure?', a: 'Encrypted Telegram sessions at rest, RBAC verified server-side on every request, httpOnly cookie auth, CSRF protection, signed DropFans webhooks, row-level isolation between agencies.' },
    { q: 'How fast is the real-time sync?', a: 'Messages arrive via MTProto in under 200ms. No polling, no delays.' },
  ],
  fr: [
    { q: "Qu'est-ce que ChatHub ?", a: "Un CRM conçu spécifiquement pour les agences de chatting qui gèrent plusieurs comptes Telegram en temps réel, avec intégration native DropFans pour l'attribution des ventes." },
    { q: 'Comment fonctionne la connexion Telegram ?', a: "Vous ajoutez votre numéro, validez avec le code SMS (et 2FA si activé), et ChatHub parle Telegram exactement comme l'app officielle iOS. Chaque compte est routé via son propre proxy mobile pour toujours apparaître depuis le même appareil." },
    { q: 'Combien de comptes Telegram puis-je connecter ?', a: "Autant que votre plan le permet. Toutes les conversations apparaissent dans une inbox unifiée, synchronisée temps réel." },
    { q: 'Puis-je contrôler ce que voient et font mes chatters ?', a: 'Oui. Notre système RBAC permet de créer des rôles custom avec 39 permissions granulaires sur 12 domaines. Chaque action est vérifiée côté serveur et loguée.' },
    { q: 'ChatHub fonctionne-t-il avec DropFans ?', a: "Oui, intégralement. Branchez votre clé API DropFans + webhook secret, et chaque vente payée est attribuée au chatter qui a envoyé le lien en premier. ChatHub est un produit indépendant non affilié à DropFans." },
    { q: 'Comment sont stockés les médias ? Dois-je les réuploader à chaque fois ?', a: "Non. Au premier envoi, Telegram retourne un file-id permanent que ChatHub met en cache. Chaque envoi suivant réutilise ce file-id — zéro réupload, zéro coût de bande passante, même pour une bibliothèque de milliers de médias." },
    { q: "Y a-t-il un essai gratuit ?", a: "Non. Vous vous abonnez et l'outil est à vous tout de suite — pas d'essai à convertir, pas de surprise au 3e jour. Vous pouvez changer de plan ou résilier quand vous voulez depuis votre compte." },
    { q: 'Comment fonctionne le paiement ?', a: "Par carte bancaire. Les factures sont générées et suivies automatiquement, et vous pouvez changer de plan ou résilier quand vous voulez depuis votre compte." },
    { q: 'Mes données sont-elles sécurisées ?', a: 'Sessions Telegram chiffrées au repos, RBAC vérifié côté serveur, auth cookie httpOnly, protection CSRF, webhooks DropFans signés, isolation au niveau ligne entre agences.' },
    { q: 'Quelle est la vitesse de synchronisation ?', a: 'Les messages arrivent via MTProto en moins de 200ms. Pas de polling, pas de délai.' },
  ],
};

function Faq() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(0);
  const lang = (i18n.resolvedLanguage || 'en').startsWith('fr') ? 'fr' : 'en';
  const items = FAQ_KEY_BY_LANG[lang];
  return (
    <section id="faq" className="max-w-4xl mx-auto px-6 py-24 reveal">
      <div className="mb-12 text-center">
        <div className="text-sm text-indigo-400 font-medium mb-3">{t('faq.kicker')}</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white">{t('faq.title')}</h2>
      </div>
      <div className="space-y-2">
        {items.map((f, i) => (
          <div key={f.q} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors">
              <span className="text-white font-medium">{f.q}</span>
              <ChevronDown size={18} className={`text-slate-400 transition-transform ${open === i ? 'rotate-180' : ''}`} />
            </button>
            {open === i && <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed fade-in">{f.a}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function Cta() {
  const { t } = useTranslation();
  return (
    <section className="max-w-5xl mx-auto px-6 py-24 reveal">
      <div className="rounded-3xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-transparent p-12 md:p-16 text-center relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">{t('cta.title1')} <br />{t('cta.title2')}</h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">{t('cta.sub')}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/subscribe?plan=solo"><Button size="lg" className="bg-indigo-500 hover:bg-indigo-600 text-white h-12 px-6">{t('cta.ctaPrimary')} <ArrowRight size={18} className="ml-1.5" /></Button></Link>
            <Link to="/auth/login"><Button size="lg" variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-12 px-6">{t('cta.ctaSecondary')}</Button></Link>
          </div>
          <div className="text-xs text-slate-500 mt-6">{t('cta.footnote')}</div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-white/5 py-12 mt-8">
      <div className="max-w-7xl mx-auto px-6 space-y-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <Logo size="sm" />
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">{t('footer.rights')}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{t('footer.legal')}</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/legal/terms" data-testid="footer-terms" className="text-slate-400 hover:text-white transition-colors">{t('footer.terms')}</Link></li>
              <li><Link to="/legal/privacy" data-testid="footer-privacy" className="text-slate-400 hover:text-white transition-colors">{t('footer.privacy')}</Link></li>
              <li><Link to="/legal/cookies" data-testid="footer-cookies" className="text-slate-400 hover:text-white transition-colors">{t('footer.cookies')}</Link></li>
              <li><Link to="/legal/legal-notice" data-testid="footer-legal-notice" className="text-slate-400 hover:text-white transition-colors">{t('footer.legalNotice')}</Link></li>
              <li><Link to="/legal/dpa" data-testid="footer-dpa" className="text-slate-400 hover:text-white transition-colors">{t('footer.dpa')}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{t('footer.company')}</div>
            <ul className="space-y-2 text-sm">
              <li><a href="mailto:contact@chathub.app" className="text-slate-400 hover:text-white transition-colors">contact@chathub.app</a></li>
              <li><a href="mailto:privacy@chathub.app" className="text-slate-400 hover:text-white transition-colors">privacy@chathub.app</a></li>
              <li><a href="mailto:abuse@chathub.app" className="text-slate-400 hover:text-white transition-colors">abuse@chathub.app</a></li>
            </ul>
          </div>
        </div>
        <p className="text-[10px] text-slate-600 max-w-3xl leading-relaxed border-t border-white/5 pt-6">
          {t('footer.disclaimer')}
        </p>
      </div>
    </footer>
  );
}
